import { ChangeEvent, useEffect, useState } from 'react'

import AppLayout from '../../components/app-layout/AppLayout'
import CardSurface from '../../components/card-surface/CardSurface'
import TenancySwitcher from '../../components/tenancy-switcher/TenancySwitcher'
import { useResidentTenancy } from '../../_hooks/use-resident-tenancy'
import {
  acceptFeedTerms,
  createCommunityPost,
  getCommunityPosts,
  getFeedAvailability,
  getFeedTermsAccepted,
  uploadCommunityPostImage,
} from '../../_data/resident'
import {
  FeedAvailability,
  RenterPost,
  ResidentPostType,
} from '../../_types/resident'
import { compressImageToDataUrl } from '../../_utilities/compress-image'
import { MAX_POST_IMAGES, getCategoryOptions } from './helpers'
import PostCard from './PostCard'
import {
  PageStack,
  PageHeader,
  PageTitle,
  PageSubtitle,
  QuietText,
  TabRow,
  TabButton,
  HeaderRow,
  SubHeading,
  PrimaryButton,
  GhostButton,
  Composer,
  FieldLabel,
  TextInput,
  TextArea,
  SelectInput,
  ComposerActions,
  PostList,
  ErrorBanner,
  NoticeBanner,
  ImageRow,
  ImageThumbWrapper,
  ImageThumb,
  ThumbRemoveButton,
  UploadingTile,
  AddImageTile,
  HiddenFileInput,
} from './styled'

const TAB_LABELS: Record<ResidentPostType, string> = {
  feed: 'Feed',
  marketplace: 'Marketplace',
  'lost-found': 'Lost & Found',
}

const TITLE_PLACEHOLDERS: Record<ResidentPostType, string> = {
  feed: 'e.g. Anyone else hearing the overnight construction?',
  marketplace: 'e.g. Free couch, great condition',
  'lost-found': 'e.g. Found: set of keys in the lobby',
}

/**
 * The renter's Community surface — three modes of the one post engine: an
 * anonymous Feed (per-building, gated + terms-accepted, with voting), a
 * real-name Marketplace, and Lost & Found. Keyed on the selected tenancy + tab.
 *
 * @returns {JSX.Element}
 */
const CommunityPage = (): JSX.Element => {
  const { selectedTenancy, selectedTenancyId, isLoadingTenancies } =
    useResidentTenancy()
  const [activeTab, setActiveTab] = useState<ResidentPostType>('marketplace')
  const [posts, setPosts] = useState<RenterPost[]>([])
  const [feedAvailability, setFeedAvailability] =
    useState<FeedAvailability | null>(null)
  const [feedTermsAccepted, setFeedTermsAccepted] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isAcceptingTerms, setIsAcceptingTerms] = useState<boolean>(false)
  const [isComposerOpen, setIsComposerOpen] = useState<boolean>(false)
  const [title, setTitle] = useState<string>('')
  const [category, setCategory] = useState<string>('')
  const [body, setBody] = useState<string>('')
  const [isPosting, setIsPosting] = useState<boolean>(false)
  const [pendingReviewNotice, setPendingReviewNotice] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [reloadToken, setReloadToken] = useState<number>(0)
  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [uploadingImageCount, setUploadingImageCount] = useState<number>(0)
  const [imageError, setImageError] = useState<string>('')

  useEffect(() => {
    if (selectedTenancyId === '') {
      setPosts([])
      setFeedAvailability(null)
      setFeedTermsAccepted(false)
      setIsLoading(false)
      return
    }

    let isMounted = true
    setIsLoading(true)
    void Promise.all([
      getCommunityPosts(selectedTenancyId, activeTab),
      activeTab === 'feed'
        ? getFeedAvailability(selectedTenancyId)
        : Promise.resolve(null),
      activeTab === 'feed'
        ? getFeedTermsAccepted(selectedTenancyId)
        : Promise.resolve(false),
    ]).then(([loadedPosts, availability, termsAccepted]) => {
      if (isMounted === false) {
        return
      }
      setPosts(loadedPosts)
      setFeedAvailability(availability)
      setFeedTermsAccepted(termsAccepted)
      setIsLoading(false)
    })

    return () => {
      isMounted = false
    }
  }, [selectedTenancyId, activeTab, reloadToken])

  const reloadPosts = (): void => {
    setReloadToken(previous => previous + 1)
  }

  const switchTab = (tab: ResidentPostType): void => {
    setActiveTab(tab)
    setIsComposerOpen(false)
    setErrorMessage('')
    setPendingReviewNotice(false)
  }

  const openComposer = (): void => {
    setTitle('')
    setBody('')
    setCategory(getCategoryOptions(activeTab)[0] ?? '')
    setErrorMessage('')
    setPendingReviewNotice(false)
    setImageUrls([])
    setUploadingImageCount(0)
    setImageError('')
    setIsComposerOpen(true)
  }

  const handleSelectImages = (event: ChangeEvent<HTMLInputElement>): void => {
    const fileList = event.target.files
    // Clear the input so re-picking the same file still fires onChange.
    event.target.value = ''
    if (fileList === null || selectedTenancyId === '') {
      return
    }

    const remainingSlots =
      MAX_POST_IMAGES - imageUrls.length - uploadingImageCount
    const filesToUpload = Array.from(fileList).slice(
      0,
      Math.max(remainingSlots, 0),
    )
    if (filesToUpload.length === 0) {
      return
    }

    setImageError('')
    filesToUpload.forEach(file => {
      setUploadingImageCount(previous => previous + 1)
      void compressImageToDataUrl(file)
        .then(dataUrl => uploadCommunityPostImage(selectedTenancyId, dataUrl))
        .then(uploadedUrl => {
          setUploadingImageCount(previous => previous - 1)
          if (uploadedUrl === null) {
            setImageError('We couldn’t upload a photo — please try again.')
            return
          }
          setImageUrls(previous =>
            previous.length >= MAX_POST_IMAGES
              ? previous
              : [...previous, uploadedUrl],
          )
        })
        .catch(() => {
          setUploadingImageCount(previous => previous - 1)
          setImageError('That file couldn’t be added — please choose an image.')
        })
    })
  }

  const handleRemoveImage = (url: string): void => {
    setImageUrls(previous => previous.filter(existing => existing !== url))
  }

  const handleAcceptTerms = (): void => {
    if (selectedTenancyId === '') {
      return
    }
    setIsAcceptingTerms(true)
    void acceptFeedTerms(selectedTenancyId).then(accepted => {
      setIsAcceptingTerms(false)
      if (accepted === true) {
        setFeedTermsAccepted(true)
      }
    })
  }

  const handleCreatePost = (): void => {
    if (selectedTenancyId === '' || title.trim() === '' || body.trim() === '') {
      setErrorMessage('Add a title and a description first.')
      return
    }
    if (uploadingImageCount > 0) {
      setErrorMessage('Hang tight — your photos are still uploading.')
      return
    }
    setIsPosting(true)
    setErrorMessage('')
    void createCommunityPost(
      selectedTenancyId,
      activeTab,
      category,
      title.trim(),
      body.trim(),
      imageUrls,
    ).then(created => {
      setIsPosting(false)
      if (created === null) {
        setErrorMessage('We couldn’t post that — please try again.')
        return
      }
      setIsComposerOpen(false)
      if (created.status !== 'visible') {
        setPendingReviewNotice(true)
      }
      reloadPosts()
    })
  }

  const isResolving = isLoadingTenancies === true || isLoading === true
  const hasPosts = posts.length > 0
  const categoryOptions = getCategoryOptions(activeTab)
  const isFeedTab = activeTab === 'feed'
  const feedEnabled =
    feedAvailability !== null && feedAvailability.isEnabled === true
  const showFeedDisabled =
    isFeedTab === true && isResolving === false && feedEnabled === false
  const showFeedTermsGate =
    isFeedTab === true &&
    isResolving === false &&
    feedEnabled === true &&
    feedTermsAccepted === false
  const showPostingSurface =
    isResolving === false &&
    (isFeedTab === false ||
      (feedEnabled === true && feedTermsAccepted === true))

  return (
    <AppLayout contentMaxWidth="standard">
      <PageStack>
        <PageHeader>
          <PageTitle>Community</PageTitle>
          <PageSubtitle>
            Talk with your neighbours, buy and sell, and reconnect lost things.
          </PageSubtitle>
        </PageHeader>

        <TenancySwitcher />

        {isLoadingTenancies === false && selectedTenancy === null ? (
          <CardSurface padding="loose">
            <QuietText>
              You don&rsquo;t have a residence yet. When your property manager
              invites you, claim the invite to join your building&rsquo;s
              community.
            </QuietText>
          </CardSurface>
        ) : null}

        {selectedTenancy !== null ? (
          <>
            <TabRow>
              <TabButton
                type="button"
                active={activeTab === 'feed'}
                onClick={() => switchTab('feed')}
              >
                Feed
              </TabButton>
              <TabButton
                type="button"
                active={activeTab === 'marketplace'}
                onClick={() => switchTab('marketplace')}
              >
                Marketplace
              </TabButton>
              <TabButton
                type="button"
                active={activeTab === 'lost-found'}
                onClick={() => switchTab('lost-found')}
              >
                Lost &amp; Found
              </TabButton>
            </TabRow>

            {isResolving === true ? (
              <CardSurface padding="loose">
                <QuietText>Loading…</QuietText>
              </CardSurface>
            ) : null}

            {showFeedDisabled === true ? (
              <CardSurface padding="loose">
                <QuietText>
                  The community feed isn&rsquo;t enabled for your building yet.
                  Your property manager can turn it on.
                </QuietText>
              </CardSurface>
            ) : null}

            {showFeedTermsGate === true ? (
              <CardSurface padding="standard">
                <SubHeading>Community feed guidelines</SubHeading>
                <QuietText>
                  The feed is for residents of your building. Posts appear under
                  a pseudonym to your neighbours, but your property manager can
                  identify the author for safety or legal reasons. This is
                  resident-created content — not posted or endorsed by the
                  property manager. Be respectful: no harassment, threats, or
                  sharing someone&rsquo;s private information. Posts are
                  screened and may be hidden or removed.
                </QuietText>
                <ComposerActions>
                  <PrimaryButton
                    type="button"
                    disabled={isAcceptingTerms === true}
                    onClick={handleAcceptTerms}
                  >
                    Accept &amp; join the feed
                  </PrimaryButton>
                </ComposerActions>
              </CardSurface>
            ) : null}

            {pendingReviewNotice === true ? (
              <NoticeBanner>
                Thanks — your post is being reviewed and will appear once it
                clears.
              </NoticeBanner>
            ) : null}

            {showPostingSurface === true ? (
              <>
                <HeaderRow>
                  <SubHeading>{TAB_LABELS[activeTab]}</SubHeading>
                  {isComposerOpen === false ? (
                    <PrimaryButton type="button" onClick={openComposer}>
                      New post
                    </PrimaryButton>
                  ) : null}
                </HeaderRow>

                {isComposerOpen === true ? (
                  <CardSurface padding="standard">
                    <Composer>
                      <div>
                        <FieldLabel>Title</FieldLabel>
                        <TextInput
                          placeholder={TITLE_PLACEHOLDERS[activeTab]}
                          value={title}
                          onChange={event => setTitle(event.target.value)}
                        />
                      </div>
                      <div>
                        <FieldLabel>Category</FieldLabel>
                        <SelectInput
                          value={category}
                          onChange={event => setCategory(event.target.value)}
                        >
                          {categoryOptions.map(option => (
                            <option key={option} value={option}>
                              {option}
                            </option>
                          ))}
                        </SelectInput>
                      </div>
                      <div>
                        <FieldLabel>Details</FieldLabel>
                        <TextArea
                          placeholder="Share the details neighbours need."
                          value={body}
                          onChange={event => setBody(event.target.value)}
                        />
                      </div>
                      {isFeedTab === false ? (
                        <div>
                          <FieldLabel>Photos (optional)</FieldLabel>
                          <ImageRow>
                            {imageUrls.map(url => (
                              <ImageThumbWrapper key={url}>
                                <ImageThumb src={url} alt="" />
                                <ThumbRemoveButton
                                  type="button"
                                  aria-label="Remove photo"
                                  onClick={() => handleRemoveImage(url)}
                                >
                                  ×
                                </ThumbRemoveButton>
                              </ImageThumbWrapper>
                            ))}
                            {[...Array(uploadingImageCount).keys()].map(
                              index => (
                                <UploadingTile key={`uploading-${index}`}>
                                  Uploading…
                                </UploadingTile>
                              ),
                            )}
                            {imageUrls.length + uploadingImageCount <
                            MAX_POST_IMAGES ? (
                              <AddImageTile>
                                <span aria-hidden="true">＋</span>
                                Photo
                                <HiddenFileInput
                                  type="file"
                                  accept="image/*"
                                  multiple
                                  onChange={handleSelectImages}
                                />
                              </AddImageTile>
                            ) : null}
                          </ImageRow>
                          {imageError !== '' ? (
                            <ErrorBanner>{imageError}</ErrorBanner>
                          ) : null}
                        </div>
                      ) : null}
                      {errorMessage !== '' ? (
                        <ErrorBanner>{errorMessage}</ErrorBanner>
                      ) : null}
                      <ComposerActions>
                        <GhostButton
                          type="button"
                          onClick={() => setIsComposerOpen(false)}
                        >
                          Cancel
                        </GhostButton>
                        <PrimaryButton
                          type="button"
                          disabled={
                            isPosting === true || uploadingImageCount > 0
                          }
                          onClick={handleCreatePost}
                        >
                          Post
                        </PrimaryButton>
                      </ComposerActions>
                    </Composer>
                  </CardSurface>
                ) : null}

                {hasPosts === false ? (
                  <CardSurface padding="loose">
                    <QuietText>
                      Nothing here yet. Be the first to post in your
                      building&rsquo;s {TAB_LABELS[activeTab].toLowerCase()}.
                    </QuietText>
                  </CardSurface>
                ) : (
                  <PostList>
                    {posts.map(renterPost => (
                      <PostCard
                        key={renterPost.post.id}
                        renterPost={renterPost}
                        tenancyId={selectedTenancyId}
                        onChanged={reloadPosts}
                      />
                    ))}
                  </PostList>
                )}
              </>
            ) : null}
          </>
        ) : null}
      </PageStack>
    </AppLayout>
  )
}

export default CommunityPage
